import { useAuth0 } from '@auth0/auth0-react'
import { apiUrl } from './apiUrl'
import { useCachedFetch } from './useCachedFetch'
import type { StaffRole } from './role'

const ROLE_CLAIM = 'https://structuralclimbing.com/role'
const GYM_ID_CLAIM = 'https://structuralclimbing.com/gym_id'

export type StaffPermissions = {
  dashboard?: boolean
  check_in?: boolean
  members?: boolean
  requests?: boolean
  plans?: boolean
  subscriptions?: boolean
  routes?: boolean
  competitions?: boolean
  statistics?: boolean
  invoices?: boolean
  exports?: boolean
  news?: boolean
  /** A trainer's own list: only the members assigned to them. */
  my_members?: boolean
  /** The Products page: selling from open carts. The catalogue stays the owner's. */
  products?: boolean
  /** The Calendar: classes, their schedule and every roster. */
  calendar?: boolean
}

export const FRONT_DESK_DEFAULTS: StaffPermissions = {
  dashboard: true,
  check_in: true,
  members: true,
  requests: true,
  plans: true,
  subscriptions: true,
  routes: false,
  competitions: false,
  statistics: false,
  invoices: false,
  exports: false,
  news: false,
  my_members: false,
  products: true,
  calendar: true,
}

export const ROUTE_SETTER_DEFAULTS: StaffPermissions = {
  dashboard: false,
  check_in: false,
  members: false,
  requests: false,
  plans: false,
  subscriptions: false,
  routes: true,
  competitions: false,
  statistics: false,
  invoices: false,
  exports: false,
  news: false,
  my_members: false,
  products: false,
  calendar: false,
}

// A trainer sees only the people assigned to them. `members` stays false on
// purpose: that is the gym-wide list. Mirrors TRAINER_DEFAULTS in the Worker —
// the two must agree or the sidebar offers pages the server refuses.
export const TRAINER_DEFAULTS: StaffPermissions = {
  dashboard: false,
  check_in: false,
  members: false,
  requests: false,
  plans: false,
  subscriptions: false,
  routes: false,
  competitions: false,
  statistics: false,
  invoices: false,
  exports: false,
  news: false,
  my_members: true,
  products: false,
  calendar: false,
}

export const STAFF_ROLE_DEFAULTS = {
  front_desk: FRONT_DESK_DEFAULTS,
  route_setter: ROUTE_SETTER_DEFAULTS,
  trainer: TRAINER_DEFAULTS,
} satisfies Record<StaffRole, StaffPermissions>

export const STAFF_ROLES = Object.keys(STAFF_ROLE_DEFAULTS) as StaffRole[]

/** Membership test rather than a bare index: `staff_role` arrives as a string,
 *  and plain property access would happily return Object.prototype members. */
export function defaultsForStaffRole(role: string | null): StaffPermissions {
  const match = STAFF_ROLES.find((k) => k === role)
  return match ? STAFF_ROLE_DEFAULTS[match] : {}
}

interface AssignmentRow {
  staff_permissions: StaffPermissions | null
  staff_role: string | null
}

function permissionsFromRow(row: AssignmentRow | null): StaffPermissions {
  // No assignment row for this gym. requireRoleOrPermission refuses in exactly
  // this case, so showing anything here would be a lie the server immediately
  // contradicts.
  if (!row) return {}
  if (row.staff_permissions) return row.staff_permissions
  // Fall back on the row's own staff_role, which is what the Worker uses.
  // Keying off the JWT claim instead let the two disagree: a row with no
  // staff_role but a claim that had one showed nav for pages the server was
  // refusing.
  return defaultsForStaffRole(row.staff_role ?? null)
}

// Returns null when loading (role is gym_staff and fetch is in-flight).
// Returns StaffPermissions once loaded.
// Returns null when role is not gym_staff (caller should check role separately).
//
// Read through the Worker, which checks the caller's own token. The key carries
// the user and the gym, so the many places that ask share one request, and a
// gym switch asks again.
export function useStaffPermissions(): StaffPermissions | null {
  const { user, getAccessTokenSilently } = useAuth0()
  const role = user?.[ROLE_CLAIM] as string | undefined
  const gymId = user?.[GYM_ID_CLAIM] as string | undefined
  const key =
    role === 'gym_staff' && user?.sub && gymId ? `staff-permissions:${user.sub}:${gymId}` : null

  const { data, error } = useCachedFetch<AssignmentRow | null>(key, async () => {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl()}/my-staff-permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error(`staff permissions ${res.status}`)
    return (await res.json()) as AssignmentRow | null
  })

  if (!key) return null
  // A failed read shows no permissions, which is what requireRoleOrPermission
  // enforces server-side anyway; showing nav we cannot back up is the worse bug.
  if (error && data === undefined) return {}
  if (data === undefined) return null
  return permissionsFromRow(data)
}
